import { useEffect, useMemo, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { colorForId, type Presence } from '@praesto/shared'

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

export interface Identity {
  id: string
  name: string
  isGuest: boolean
}

export interface Collaboration {
  doc: Y.Doc
  provider: WebsocketProvider | null
  status: ConnectionStatus
}

function websocketBase(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws/board`
}

/**
 * Verbindet ein Yjs-Dokument mit dem Board-Raum auf dem Server.
 *
 * Der Client nutzt `y-websocket`, weil unser Server exakt dessen Protokoll
 * spricht (Nachrichtentyp 0 = Sync, 1 = Awareness). Eigenbau war nur auf der
 * Serverseite nötig — dort wegen der Gast-Leserechte.
 *
 * Wichtig ist die Trennung der drei Lebenszyklen: Dokument, Verbindung und
 * Presence hängen an unterschiedlichen Dingen. Die Identität steht erst fest,
 * wenn `/api/boards/:id` geantwortet hat — würde sie die Verbindung neu
 * aufbauen, risse sie das Dokument mit und nichts würde mehr synchronisieren.
 */
export function useCollaboration(boardId: string, me: Identity): Collaboration {
  const doc = useMemo(() => new Y.Doc(), [boardId])
  const [provider, setProvider] = useState<WebsocketProvider | null>(null)
  const [status, setStatus] = useState<ConnectionStatus>('connecting')

  // 1. Dokument: lebt so lange wie das Board, nicht wie die Verbindung.
  useEffect(() => () => doc.destroy(), [doc])

  // 2. Verbindung: hängt ausschließlich am Board.
  useEffect(() => {
    const instance = new WebsocketProvider(websocketBase(), boardId, doc)

    const onStatus = ({ status: next }: { status: string }): void => {
      setStatus(
        next === 'connected' ? 'connected' : next === 'connecting' ? 'connecting' : 'disconnected',
      )
    }
    instance.on('status', onStatus)
    setProvider(instance)

    return () => {
      instance.off('status', onStatus)
      // Presence abmelden, bevor die Verbindung fällt — sonst bleibt bei den
      // anderen kurzzeitig ein Geister-Cursor stehen.
      instance.awareness.setLocalState(null)
      instance.destroy()
      setProvider(null)
    }
  }, [boardId, doc])

  // 3. Presence: darf sich beliebig oft ändern, ohne etwas neu aufzubauen.
  useEffect(() => {
    if (!provider) return
    const current = provider.awareness.getLocalState() as Presence | null
    const presence: Presence = {
      user: { id: me.id, name: me.name, color: colorForId(me.id), isGuest: me.isGuest },
      cursor: current?.cursor ?? null,
      viewport: current?.viewport ?? null,
      focus: current?.focus ?? null,
      presenting: current?.presenting ?? false,
      following: current?.following ?? true,
      pointer: current?.pointer ?? null,
    }
    provider.awareness.setLocalState(presence)
  }, [provider, me.id, me.name, me.isGuest])

  return { doc, provider, status }
}
